import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import MoreButton from '../components/MoreButton';
import AppDrawer from '../components/AppDrawer';
import FeedbackLink from '../components/FeedbackLink';
import DesktopNav from '../components/DesktopNav';
import { AlertBanner } from '../components/AlertBanner';
import { useSchedule } from '../context/ScheduleContext';
import { groupByWeekday, parseWeekdays, formatTime12, formatLocation, getTerms, pickDefaultTerm, termKeyForCourse, formatMeetingDateRange, flattenMeetings, toLocalDate, formatShortDate } from '../utils/schedule';
import './SchedulePage.css';

const BackArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const CreditsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 5.5C3 4.12 4.12 3 5.5 3H12v16H5.5A2.5 2.5 0 0 1 3 16.5v-11z"></path>
    <path d="M12 3h6.5A2.5 2.5 0 0 1 21 5.5v11A2.5 2.5 0 0 1 18.5 19H12"></path>
  </svg>
);

const MoodleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

const DegreePlanIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
    <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
  </svg>
);

const formatCredits = (credits) => `${Number.parseFloat(credits).toFixed(1)} credits`;

const meetingSummary = (m) => {
  const days = parseWeekdays(m.weekdays);
  const dayLabel = days.length ? days.map((d) => d.slice(0, 3)).join(', ') : (m.instruction_desc || 'Online');
  return `${dayLabel} · ${formatTime12(m.start_time)} – ${formatTime12(m.end_time)}`;
};

const SchedulePage = () => {
  const navigate = useNavigate();
  const { courses, loading, error, refetch } = useSchedule();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [view, setView] = useState('schedule');
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [expandedCourses, setExpandedCourses] = useState(new Set());

  const toggleCourseExpanded = (courseCode) => {
    setExpandedCourses((prev) => {
      const next = new Set(prev);
      if (next.has(courseCode)) next.delete(courseCode);
      else next.add(courseCode);
      return next;
    });
  };

  const onTabKeyDown = (e) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setView((v) => (v === 'schedule' ? 'courses' : 'schedule'));
    }
  };

  const terms = useMemo(() => getTerms(courses), [courses]);
  const activeTerm = selectedTerm ?? pickDefaultTerm(terms);
  const termCourses = useMemo(
    () => courses.filter((c) => termKeyForCourse(c) === activeTerm),
    [courses, activeTerm]
  );
  const meetings = useMemo(() => flattenMeetings(termCourses), [termCourses]);

  const days = groupByWeekday(meetings);
  const totalCredits = Math.round(termCourses.reduce((sum, c) => sum + (Number.parseFloat(c.credits) || 0), 0) * 10) / 10;
  const maxEndDate = useMemo(
    () => termCourses.reduce((max, c) => {
      const d = toLocalDate(c.course_end_date);
      return d && (!max || d > max) ? d : max;
    }, null),
    [termCourses]
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="sched-state">
          <div className="loading-spinner"></div>
          <p>Loading your courses…</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="sched-state sched-state--error">
          <p>{error}</p>
          <button className="btn-retry" onClick={refetch}>Try Again</button>
        </div>
      );
    }
    if (!courses.length) {
      return (
        <div className="sched-state">
          <MoodleIcon />
          <p>No enrolled courses found.</p>
        </div>
      );
    }

    return (
      <>
        {terms.length > 1 && (
          <div className="sched-term">
            <label htmlFor="term-select" className="sched-term-label">Term</label>
            <select
              id="term-select"
              className="sched-term-select"
              value={activeTerm || ''}
              onChange={(e) => setSelectedTerm(e.target.value)}
            >
              {terms.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>
        )}
        <div className="sched-segmented" role="tablist" aria-label="Schedule views" onKeyDown={onTabKeyDown}>
          <button
            role="tab"
            id="tab-schedule"
            aria-selected={view === 'schedule'}
            aria-controls="panel-schedule"
            tabIndex={view === 'schedule' ? 0 : -1}
            className={`sched-segment${view === 'schedule' ? ' is-active' : ''}`}
            onClick={() => setView('schedule')}
          >
            Schedule
          </button>
          <button
            role="tab"
            id="tab-courses"
            aria-selected={view === 'courses'}
            aria-controls="panel-courses"
            tabIndex={view === 'courses' ? 0 : -1}
            className={`sched-segment${view === 'courses' ? ' is-active' : ''}`}
            onClick={() => setView('courses')}
          >
            Courses
          </button>
        </div>

        {view === 'schedule' ? (
          <div role="tabpanel" id="panel-schedule" aria-labelledby="tab-schedule" tabIndex={0} className="sched-days">
            {days.length === 0 && (
              <div className="sched-state">
                <p>No scheduled class times this term.</p>
              </div>
            )}
            {days.map(({ day, items }) => (
              <section key={day} className="sched-day">
                <h2 className="sched-day-header">{day}</h2>
                <div className="sched-day-list">
                  {items.map((m) => {
                    const dateLabel = formatMeetingDateRange(m);
                    return (
                      <div key={m.id} className="sched-card">
                        <div className="sched-card-head">
                          <h3 className="sched-code">{m.course_code}</h3>
                          {m.course_name && <span className="sched-name">{m.course_name}</span>}
                        </div>
                        <div className="sched-meta">
                          <span className="sched-meta-item"><ClockIcon /> {formatTime12(m.start_time)} – {formatTime12(m.end_time)}</span>
                          <span className="sched-meta-item"><LocationIcon /> {formatLocation(m.building, m.room)}</span>
                          {dateLabel && <span className="sched-meta-item"><CalendarIcon /> {dateLabel}</span>}
                        </div>
                        {m.instruction_desc && <span className="sched-pill">{m.instruction_desc}</span>}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div role="tabpanel" id="panel-courses" aria-labelledby="tab-courses" tabIndex={0} className="sched-courses">
            <div className="sched-summary">
              <div className="sched-summary-stat">
                <span className="sched-summary-value">{termCourses.length}</span>
                <span className="sched-summary-label">{termCourses.length === 1 ? 'Course' : 'Courses'}</span>
              </div>
              <div className="sched-summary-stat">
                <span className="sched-summary-value">{totalCredits}</span>
                <span className="sched-summary-label">{totalCredits === 1 ? 'Credit' : 'Credits'}</span>
              </div>
            </div>
            <div className="sched-cta-group">
              <Link to="/moodle" className="sched-moodle-all">
                <MoodleIcon /> Open Moodle
                <span className="sched-ext">Opens external site</span>
              </Link>
              <Link to="/degree-plan" className="sched-moodle-all">
                <DegreePlanIcon /> Degree Plan
                <span className="sched-ext">Opens external site</span>
              </Link>
            </div>
            {termCourses.map((c) => {
              const multiType = c.instruction_types.length > 1;
              const endDate = toLocalDate(c.course_end_date);
              const endsEarly = endDate && maxEndDate && endDate < maxEndDate;
              const isExpanded = expandedCourses.has(c.course_code);
              const hasMany = c.meetings.length > 2;
              const visibleMeetings = hasMany && !isExpanded ? c.meetings.slice(0, 2) : c.meetings;
              return (
                <div key={c.course_code} className="sched-card sched-card--course">
                  <div className="sched-card-head">
                    <h3 className="sched-code">{c.course_code}</h3>
                    {c.course_name && <span className="sched-name">{c.course_name}</span>}
                  </div>
                  {(c.instruction_types.length > 0 || endsEarly) && (
                    <div className="sched-pills">
                      {c.instruction_types.map((t) => (
                        <span key={t} className="sched-pill">{t}</span>
                      ))}
                      {endsEarly && (
                        <span className="sched-pill sched-pill--end-date">Ends {formatShortDate(endDate)}</span>
                      )}
                    </div>
                  )}
                  <div className="sched-meta">
                    {c.credits && <span className="sched-meta-item"><CreditsIcon /> {formatCredits(c.credits)}</span>}
                    {c.meetings.length === 0 ? (
                      <span className="sched-meta-item">No scheduled meeting times</span>
                    ) : (
                      <>
                        {visibleMeetings.map((m) => {
                          const dateLabel = formatMeetingDateRange(m);
                          return (
                            <div key={m.id} className="sched-meeting">
                              {multiType && m.instruction_desc && (
                                <span className="sched-meeting-cat">{m.instruction_desc}</span>
                              )}
                              <span className="sched-meta-item"><ClockIcon /> {meetingSummary(m)}</span>
                              <span className="sched-meta-item"><LocationIcon /> {formatLocation(m.building, m.room)}</span>
                              {dateLabel && <span className="sched-meta-item"><CalendarIcon /> {dateLabel}</span>}
                            </div>
                          );
                        })}
                        {hasMany && (
                          <button className="sched-meetings-toggle" onClick={() => toggleCourseExpanded(c.course_code)}>
                            {isExpanded ? 'Show less' : `See all ${c.meetings.length} dates/times`}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="schedule-page">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <button className="back-button" onClick={() => navigate('/dashboard')} aria-label="Go back to dashboard">
            <BackArrowIcon />
          </button>
          <h1>Courses &amp; Schedule</h1>
        </div>
        <div className="dashboard-header-right">
          <MoreButton onClick={() => setDrawerOpen(true)} />
        </div>
      </header>
      <AlertBanner />
      <DesktopNav />
      <div className="schedule-content">
        {renderContent()}
        <FeedbackLink text="Course or schedule info look wrong? Let us know" />
      </div>
      <AppDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} onOpen={() => setDrawerOpen(true)} />
      <BottomNav />
    </div>
  );
};

export default SchedulePage;
